import type { Identifier, XYCoord } from 'dnd-core'
import React, { FC, PropsWithChildren } from 'react'
import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import Grid, { GridProps } from '@mui/material/Grid'

type DragAndDropItemWrapperProps = GridProps & {
  moveItem: (dragIndex: number, hoverIndex: number) => void
  dragStart: () => void
  dragEnd: () => void
  abortDragging: boolean
  type: string
  index: number
  id: string
}

interface DragItem {
  index: number
  id: string
  type: string
}

export const DragAndDropItemWrapper: FC<PropsWithChildren<DragAndDropItemWrapperProps>> = ({
  moveItem,
  dragStart,
  dragEnd,
  type,
  index,
  id,
  abortDragging,
  children,
  sx,
  ...gridProps
}) => {
  const ref = useRef<HTMLDivElement>(null)

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: type,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      }
    },
    hover(item: DragItem, monitor) {
      if (!ref.current || abortDragging) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      dragStart()
      // Time to actually perform the action
      moveItem(dragIndex, hoverIndex)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
    drop(item: DragItem, monitor) {
      dragEnd()
    }
  })

  const [{ isDragging }, drag] = useDrag({
    type: type,
    item: () => {
      return { id, index }
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging()
    })
  })

  const opacity = isDragging ? 0 : 1
  drag(drop(abortDragging ? null : ref))

  return (
    <Grid
      {...gridProps}
      sx={{ opacity, cursor: 'move', ...sx }}
      data-handler-id={handlerId}
      ref={ref}
    >
      {children}
    </Grid>
  )
}
